'use client'

import { useMemo, useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  X,
  Check,
  Brain,
  Target,
  Shield,
  Route,
  Sparkles,
  ChevronRight,
  ChevronLeft,
  UserRound,
  MessageSquareMore,
  Users,
  BadgeHelp,
  Wrench,
  GraduationCap,
  LifeBuoy,
  ShoppingCart,
  TriangleAlert,
  FileStack,
  Upload,
  FileText,
  Link as LinkIcon,
  Trash2,
  Loader2,
} from 'lucide-react'

/* ------------------ Tone presets ------------------ */

const tonePresets = [
  {
    id: 'pro_claro',
    title: 'Profesional y claro',
    description: 'Directo, estructurado y orientado a resultados',
    prompt:
      'Habla de forma profesional, clara y sin rodeos. Prioriza respuestas concretas, bien estructuradas y accionables.',
  },
  {
    id: 'amigable',
    title: 'Cercano y amigable',
    description: 'Tono humano, empático y accesible',
    prompt:
      'Habla de forma cercana, empática y motivadora. Usa un lenguaje sencillo, humano y fácil de entender.',
  },
  {
    id: 'mentor',
    title: 'Mentor experto',
    description: 'Guía con autoridad y visión estratégica',
    prompt:
      'Actúa como un mentor experto. Explica el porqué de las cosas, guía con criterio y ayuda a tomar mejores decisiones.',
  },
  {
    id: 'ventas',
    title: 'Ventas y conversión',
    description: 'Persuasivo y orientado a acción',
    prompt:
      'Enfoca las respuestas en beneficios, objeciones, claridad de valor y llamados a la acción sin sonar agresivo.',
  },
  {
    id: 'soporte',
    title: 'Soporte y ayuda',
    description: 'Paciente, detallado y resolutivo',
    prompt:
      'Prioriza ayudar y resolver dudas paso a paso, con paciencia, claridad y foco en la solución.',
  },
]

/* ------------------ Options ------------------ */

const businessAreas = [
  {
    id: 'marca',
    title: 'Marca y posicionamiento',
    description: 'Quiénes somos, promesa, valores y forma de comunicar',
    icon: Sparkles,
  },
  {
    id: 'ofertas',
    title: 'Ofertas y productos',
    description: 'Qué vende el negocio y cómo orientar a la mejor opción',
    icon: ShoppingCart,
  },
  {
    id: 'onboarding',
    title: 'Onboarding',
    description: 'Primeros pasos, activación y acompañamiento inicial',
    icon: UserRound,
  },
  {
    id: 'soporte',
    title: 'Soporte general',
    description: 'Dudas frecuentes, accesos, incidencias y ayuda básica',
    icon: LifeBuoy,
  },
  {
    id: 'faq',
    title: 'Preguntas frecuentes',
    description: 'Respuestas generales y objeciones comunes',
    icon: BadgeHelp,
  },
  {
    id: 'comunidad',
    title: 'Comunidad y experiencia',
    description: 'Normas, participación, beneficios y dinámica de la comunidad',
    icon: Users,
  },
]

const specialistSuggestions = [
  {
    id: 'ventas',
    title: 'Agente de ventas',
    description: 'Comparar ofertas, resolver objeciones y ayudar al cierre',
    icon: ShoppingCart,
  },
  {
    id: 'soporte',
    title: 'Agente de soporte',
    description: 'Resolver problemas técnicos o de cuenta',
    icon: Wrench,
  },
  {
    id: 'onboarding',
    title: 'Agente de onboarding',
    description: 'Guiar nuevos usuarios paso a paso',
    icon: UserRound,
  },
  {
    id: 'cursos',
    title: 'Agente de cursos o formación',
    description: 'Responder dudas profundas sobre lecciones, módulos o programas',
    icon: GraduationCap,
  },
  {
    id: 'comunidad',
    title: 'Agente de comunidad',
    description: 'Acompañar participación, normas, beneficios y engagement',
    icon: Users,
  },
]

const masterRulesBase = [
  'No inventar funciones, precios o beneficios que no estén confirmados',
  'No prometer resultados garantizados',
  'No dar asesoría legal, médica o financiera',
  'Escalar a soporte humano cuando no tenga certeza suficiente',
  'Responder solo lo necesario y luego sugerir el siguiente paso',
  'Hacer preguntas antes de recomendar algo importante',
]

const escalationTriggersBase = [
  'Cuando el usuario reporta un error técnico específico',
  'Cuando el usuario pide ayuda sobre pagos, cargos o facturación',
  'Cuando el usuario solicita una excepción, reembolso o revisión manual',
  'Cuando hay riesgo legal, médico, financiero o reputacional',
  'Cuando el usuario expresa molestia, frustración fuerte o desconfianza',
]

const routingPresets = [
  {
    id: 'orientar',
    title: 'Orientar y dirigir',
    description: 'Entender al usuario, ubicarlo y llevarlo al siguiente paso lógico',
  },
  {
    id: 'resolver_general',
    title: 'Resolver dudas generales',
    description: 'Responder preguntas frecuentes y explicar lo esencial con claridad',
  },
  {
    id: 'recomendar',
    title: 'Recomendar la mejor ruta',
    description: 'Sugerir producto, servicio, contenido o acción según el perfil',
  },
  {
    id: 'delegar',
    title: 'Delegar a especialistas',
    description: 'Detectar cuándo una conversación corresponde a otro agente o humano',
  },
]

const onboardingQuestionPresets = [
  '¿Qué estás buscando lograr exactamente?',
  '¿En qué etapa estás ahora mismo?',
  '¿Quieres ayuda para empezar, comparar opciones o resolver una duda puntual?',
  '¿Prefieres una explicación rápida o una guía paso a paso?',
]

/* ------------------ Steps ------------------ */

const steps = [
  'Personalidad',
  'Misión',
  'Dominio',
  'Límites',
  'Routing',
  'Conocimiento',
  'Confirmar',
]

/* ------------------ Types ------------------ */

type OptionCard = {
  id: string
  title: string
  description: string
}

type UploadedDoc = {
  id?: number
  name: string
  type: string
  status?: 'pending' | 'processing' | 'ready' | 'error'
  key?: string
  url?: string
}

type TrainingData = {
  toneId: string
  tonePrompt: string

  masterName: string
  mission: string
  successDefinition: string

  businessAreas: string[]
  knowledgeCore: string
  shouldAskBeforeAnswer: boolean

  rules: string[]
  extraRules: string
  escalationTriggers: string[]
  extraEscalation: string

  routing: string[]
  onboardingQuestions: string[]
  extraOnboardingQuestions: string
  specialistSuggestions: string[]

  notes: string
  links: string[]
  documents: UploadedDoc[]
}

/* ------------------ Helpers ------------------ */

function toggleArrayValue(list: string[], value: string) {
  return list.includes(value) ? list.filter(v => v !== value) : [...list, value]
}

function normalizeMultilineLines(input: string) {
  return input
    .split('\n')
    .map(line => line.trim())
    .filter(Boolean)
}

function buildRulesString(data: TrainingData) {
  return [
    ...data.rules,
    ...normalizeMultilineLines(data.extraRules),
    '',
    'ESCALAMIENTO A HUMANO O ESPECIALISTA:',
    ...data.escalationTriggers,
    ...normalizeMultilineLines(data.extraEscalation),
  ]
    .filter(Boolean)
    .join('\n')
}

function buildMasterPromptDraft(data: TrainingData) {
  const selectedAreas = businessAreas
    .filter(a => data.businessAreas.includes(a.id))
    .map(a => a.title)

  const selectedRouting = routingPresets
    .filter(r => data.routing.includes(r.id))
    .map(r => r.title)

  const selectedSpecialists = specialistSuggestions
    .filter(s => data.specialistSuggestions.includes(s.id))
    .map(s => s.title)

  const onboardingQuestions = [
    ...data.onboardingQuestions,
    ...normalizeMultilineLines(data.extraOnboardingQuestions),
  ]

  return `
IDENTIDAD
Nombre del agente: ${data.masterName || 'Agente Master'}
Rol: Cerebro principal de la comunidad. Actúas como la capa estratégica que recibe al usuario, entiende su intención, responde lo general y decide el siguiente paso.

TONO
${data.tonePrompt}

MISIÓN
${data.mission}

CÓMO SE VE EL ÉXITO
${data.successDefinition}

ÁREAS QUE DOMINAS
${selectedAreas.length ? selectedAreas.join(', ') : 'General'}

NÚCLEO DE CONOCIMIENTO ESPERADO
${data.knowledgeCore || 'Sin definir aún.'}

FORMA DE ACTUAR
- ${data.shouldAskBeforeAnswer ? 'Debes hacer preguntas antes de recomendar algo importante.' : 'Puedes responder directamente cuando la intención esté clara.'}
- Responde con claridad, sin saturar.
- Prioriza orientar, no abrumar.
- Sugiere siempre el siguiente paso lógico.
- No intentes resolver todo si el tema pertenece a un especialista.

ROUTING PRINCIPAL
${selectedRouting.length ? selectedRouting.map(item => `- ${item}`).join('\n') : '- Orientar al usuario'}

AGENTES ESPECIALIZADOS SUGERIDOS
${selectedSpecialists.length ? selectedSpecialists.map(item => `- ${item}`).join('\n') : '- Aún no definidos'}

PREGUNTAS INICIALES SUGERIDAS
${onboardingQuestions.length ? onboardingQuestions.map(q => `- ${q}`).join('\n') : '- ¿Cómo te puedo ayudar hoy?'}

REGLAS Y LÍMITES
${buildRulesString(data)}
`.trim()
}

function StepShell({
  title,
  description,
  children,
}: {
  title: string
  description?: string
  children: React.ReactNode
}) {
  return (
    <div className="space-y-5">
      <div className="space-y-1">
        <h2 className="text-xl font-semibold text-zinc-900 dark:text-zinc-100">{title}</h2>
        {description ? (
          <p className="text-sm text-zinc-600 dark:text-zinc-400">{description}</p>
        ) : null}
      </div>
      {children}
    </div>
  )
}

function ToggleCard({
  item,
  selected,
  onClick,
}: {
  item: OptionCard
  selected: boolean
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={[
        'w-full rounded-2xl border p-4 text-left transition',
        selected
          ? 'border-black bg-zinc-50 shadow-sm dark:border-white dark:bg-zinc-900'
          : 'border-zinc-200 hover:border-zinc-400 hover:bg-zinc-50/60 dark:border-zinc-800 dark:hover:border-zinc-600 dark:hover:bg-zinc-900/70',
      ].join(' ')}
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="font-medium text-zinc-900 dark:text-zinc-100">{item.title}</div>
          <div className="mt-1 text-sm text-zinc-600 dark:text-zinc-400">{item.description}</div>
        </div>
        {selected ? <Check className="h-4 w-4 shrink-0 text-zinc-900 dark:text-zinc-100" /> : null}
      </div>
    </button>
  )
}

function MultiSelectIconCard({
  id,
  title,
  description,
  icon: Icon,
  selected,
  onClick,
}: {
  id: string
  title: string
  description: string
  icon: any
  selected: boolean
  onClick: (id: string) => void
}) {
  return (
    <button
      type="button"
      onClick={() => onClick(id)}
      className={[
        'w-full rounded-2xl border p-4 text-left transition',
        selected
          ? 'border-black bg-zinc-50 shadow-sm dark:border-white dark:bg-zinc-900'
          : 'border-zinc-200 hover:border-zinc-400 hover:bg-zinc-50/60 dark:border-zinc-800 dark:hover:border-zinc-600 dark:hover:bg-zinc-900/70',
      ].join(' ')}
    >
      <div className="flex items-start gap-3">
        <div
          className={[
            'mt-0.5 rounded-xl border p-2',
            selected
              ? 'border-black bg-white dark:border-white dark:bg-zinc-950'
              : 'border-zinc-200 bg-white dark:border-zinc-800 dark:bg-zinc-950',
          ].join(' ')}
        >
          <Icon className="h-4 w-4 text-zinc-800 dark:text-zinc-200" />
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between gap-3">
            <div className="font-medium text-zinc-900 dark:text-zinc-100">{title}</div>
            {selected ? <Check className="h-4 w-4 shrink-0 text-zinc-900 dark:text-zinc-100" /> : null}
          </div>
          <div className="mt-1 text-sm text-zinc-600 dark:text-zinc-400">{description}</div>
        </div>
      </div>
    </button>
  )
}

function SmallPill({
  active,
  label,
  onClick,
}: {
  active: boolean
  label: string
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={[
        'rounded-full border px-3 py-2 text-sm transition',
        active
          ? 'border-black bg-black text-white dark:border-white dark:bg-white dark:text-black'
          : 'border-zinc-300 bg-white text-zinc-700 hover:border-zinc-500 dark:border-zinc-700 dark:bg-zinc-950 dark:text-zinc-300 dark:hover:border-zinc-500',
      ].join(' ')}
    >
      {label}
    </button>
  )
}

/* ------------------ Component ------------------ */

export function AIMasterTrainingOverlay({
  agentId,
  sellerId,
  onClose,
  onSaved,
}: {
  agentId: number
  sellerId: number
  onClose: () => void
  onSaved?: () => void
}) {
  const MASTER_USER_ID = 0

  const [step, setStep] = useState(0)
  const [loading, setLoading] = useState(false)
  const [savingKnowledge, setSavingKnowledge] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [linkInput, setLinkInput] = useState('')

  const [data, setData] = useState<TrainingData>({
    toneId: '',
    tonePrompt: '',

    masterName: 'Samantha Master',
    mission: '',
    successDefinition: '',

    businessAreas: [],
    knowledgeCore: '',
    shouldAskBeforeAnswer: true,

    rules: [
      'No inventar funciones, precios o beneficios que no estén confirmados',
      'Escalar a soporte humano cuando no tenga certeza suficiente',
    ],
    extraRules: '',
    escalationTriggers: [],
    extraEscalation: '',

    routing: ['orientar', 'resolver_general', 'delegar'],
    onboardingQuestions: [
      '¿Qué estás buscando lograr exactamente?',
      '¿Quieres que te oriente, compare opciones o resuelva una duda puntual?',
    ],
    extraOnboardingQuestions: '',
    specialistSuggestions: [],

    notes: '',
    links: [],
    documents: [],
  })

  const masterPromptDraft = useMemo(() => buildMasterPromptDraft(data), [data])

  const canContinue = useMemo(() => {
    if (step === 0) return Boolean(data.toneId && data.masterName.trim())
    if (step === 1) return Boolean(data.mission.trim() && data.successDefinition.trim())
    if (step === 2) return data.businessAreas.length > 0
    if (step === 3) return data.rules.length > 0
    if (step === 4) return data.routing.length > 0
    if (step === 5) return true
    return true
  }, [step, data])

  function selectTone(preset: (typeof tonePresets)[number]) {
    setData(prev => ({
      ...prev,
      toneId: preset.id,
      tonePrompt: preset.prompt,
    }))
  }

  function toggleBusinessArea(value: string) {
    setData(prev => ({
      ...prev,
      businessAreas: toggleArrayValue(prev.businessAreas, value),
    }))
  }

  function toggleRule(value: string) {
    setData(prev => ({
      ...prev,
      rules: toggleArrayValue(prev.rules, value),
    }))
  }

  function toggleEscalation(value: string) {
    setData(prev => ({
      ...prev,
      escalationTriggers: toggleArrayValue(prev.escalationTriggers, value),
    }))
  }

  function toggleRouting(value: string) {
    setData(prev => ({
      ...prev,
      routing: toggleArrayValue(prev.routing, value),
    }))
  }

  function toggleOnboardingQuestion(value: string) {
    setData(prev => ({
      ...prev,
      onboardingQuestions: toggleArrayValue(prev.onboardingQuestions, value),
    }))
  }

  function toggleSpecialist(value: string) {
    setData(prev => ({
      ...prev,
      specialistSuggestions: toggleArrayValue(prev.specialistSuggestions, value),
    }))
  }

  function addLink() {
    const value = linkInput.trim()
    if (!value) return
    setData(prev => ({
      ...prev,
      links: [...prev.links, value],
    }))
    setLinkInput('')
  }

  function removeLink(index: number) {
    setData(prev => ({
      ...prev,
      links: prev.links.filter((_, i) => i !== index),
    }))
  }

  async function uploadOneFile(file: File) {
    const presignRes = await fetch('/api/uploads/member/presign', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        scope: 'agent_knowledge',
        resourceId: String(agentId),
        filename: file.name,
        contentType: file.type || 'application/octet-stream',
        size: file.size,
      }),
    })

    const presignJson = await presignRes.json().catch(() => null)

    if (!presignRes.ok || !presignJson?.ok) {
      throw new Error(presignJson?.error || 'No se pudo generar upload URL')
    }

    const putRes = await fetch(presignJson.uploadUrl, {
      method: 'PUT',
      headers: {
        'Content-Type': file.type || 'application/octet-stream',
      },
      body: file,
    })

    if (!putRes.ok) {
      throw new Error('Falló la subida a S3')
    }

    const commitRes = await fetch('/api/uploads/member/commit', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        key: presignJson.key,
        scope: 'agent_knowledge',
        resourceId: String(agentId),
      }),
    })

    const commitJson = await commitRes.json().catch(() => null)

    if (!commitRes.ok || !commitJson?.ok) {
      throw new Error(commitJson?.error || 'No se pudo confirmar el archivo')
    }

    const registerRes = await fetch('/api/ai/agents/documents/register-upload', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        agentId,
        sellerId,
        userId: MASTER_USER_ID,
        filename: file.name,
        filePath: presignJson.key,
        fileType: file.type || 'application/octet-stream',
      }),
    })

    const registerJson = await registerRes.json().catch(() => null)

    if (!registerRes.ok || !registerJson?.ok) {
      throw new Error(registerJson?.error || 'No se pudo registrar el documento')
    }

    return {
      id: registerJson.document?.id,
      name: registerJson.document?.filename || file.name,
      type: registerJson.document?.file_type || file.type || 'file',
      status: registerJson.document?.status || 'pending',
      key: presignJson.key,
      url: commitJson.url,
    } as UploadedDoc
  }

  async function handleFiles(files: FileList | null) {
    if (!files?.length) return

    setUploading(true)
    try {
      for (const file of Array.from(files)) {
        const uploaded = await uploadOneFile(file)
        setData(prev => ({
          ...prev,
          documents: [...prev.documents, uploaded],
        }))
      }
    } catch (err) {
      console.error('[AIMasterTrainingOverlay] upload', err)
    } finally {
      setUploading(false)
    }
  }

  async function saveKnowledgeOnly() {
    if (!data.notes.trim() && data.links.length === 0) return

    const res = await fetch('/api/ai/agents/documents/save-notes-links', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        agentId,
        sellerId,
        userId: MASTER_USER_ID,
        notes: data.notes,
        links: data.links,
      }),
    })

    const json = await res.json().catch(() => null)

    if (!res.ok || !json?.ok) {
      throw new Error(json?.error || 'No se pudo guardar la base de conocimiento')
    }
  }

  async function handleFinish() {
    setLoading(true)

    const selectedAreaLabels = businessAreas
      .filter(a => data.businessAreas.includes(a.id))
      .map(a => a.title)

    const selectedSpecialistLabels = specialistSuggestions
      .filter(s => data.specialistSuggestions.includes(s.id))
      .map(s => s.title)

    const selectedRoutingLabels = routingPresets
      .filter(r => data.routing.includes(r.id))
      .map(r => r.title)

    const onboardingQuestions = [
      ...data.onboardingQuestions,
      ...normalizeMultilineLines(data.extraOnboardingQuestions),
    ]

    const rulesString = buildRulesString(data)

    const payload = {
      type: 'master',
      config: {
        tone_prompt: data.tonePrompt,
        goal: data.mission,
        rules: rulesString,

        master_name: data.masterName,
        success_definition: data.successDefinition,
        should_ask_before_answer: data.shouldAskBeforeAnswer,

        brain_blueprint: {
          version: 1,
          kind: 'master_brain',
          agent_id: agentId,
          master_name: data.masterName,
          scope: 'Cerebro estratégico principal de comunidad',
          mission: data.mission,
          success_definition: data.successDefinition,
          business_areas: selectedAreaLabels,
          routing: selectedRoutingLabels,
          specialist_suggestions: selectedSpecialistLabels,
          onboarding_questions: onboardingQuestions,
          escalation_triggers: [
            ...data.escalationTriggers,
            ...normalizeMultilineLines(data.extraEscalation),
          ],
          knowledge_core_summary: data.knowledgeCore,
        },

        routing_rules: {
          master_handles: selectedAreaLabels,
          delegates_to_specialists: selectedSpecialistLabels,
          ask_before_answer: data.shouldAskBeforeAnswer,
        },

        knowledge_core: {
          summary: data.knowledgeCore,
          recommended_foundational_docs: [
            'Identidad de marca o comunidad',
            'Oferta principal y secundarias',
            'FAQ general',
            'Objeciones comunes',
            'Proceso de onboarding',
            'Políticas y límites del negocio',
          ],
        },

        onboarding_behavior: {
          opening_questions: onboardingQuestions,
        },

        master_prompt_draft: masterPromptDraft,
      },
    }

    try {
      const configRes = await fetch('/api/ai/agents/update', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      })

      const configJson = await configRes.json().catch(() => null)

      if (!configRes.ok || !configJson?.ok) {
        throw new Error(configJson?.error || 'No se pudo guardar la configuración del Agente Master')
      }

      setSavingKnowledge(true)
      await saveKnowledgeOnly()
      setSavingKnowledge(false)

      onSaved?.()
      onClose()
    } catch (err) {
      console.error('[AIMasterTrainingOverlay] save', err)
    } finally {
      setSavingKnowledge(false)
      setLoading(false)
    }
  }

  return (
    <motion.div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-md p-4"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <motion.div
        className="relative w-full max-w-5xl overflow-hidden rounded-3xl border border-zinc-200 bg-white shadow-2xl dark:border-zinc-800 dark:bg-zinc-950"
        initial={{ scale: 0.96, y: 30 }}
        animate={{ scale: 1, y: 0 }}
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute right-4 top-4 z-10 rounded-full p-2 text-zinc-500 transition hover:bg-zinc-100 hover:text-zinc-900 dark:text-zinc-400 dark:hover:bg-zinc-900 dark:hover:text-zinc-100"
        >
          <X className="h-5 w-5" />
        </button>

        <div className="grid min-h-[760px] grid-cols-1 lg:grid-cols-[260px_1fr]">
          <aside className="border-b bg-zinc-50 p-6 dark:border-zinc-800 dark:bg-zinc-900 lg:border-b-0 lg:border-r">
            <div className="mb-6">
              <div className="flex items-center gap-2 text-zinc-900 dark:text-zinc-100">
                <div className="rounded-2xl bg-black p-2 text-white dark:bg-white dark:text-black">
                  <Brain className="h-4 w-4" />
                </div>
                <div>
                  <h1 className="text-lg font-semibold">Agente Master</h1>
                  <p className="text-sm text-zinc-600 dark:text-zinc-400">
                    Configura cerebro y conocimiento de la comunidad
                  </p>
                </div>
              </div>
            </div>

            <div className="space-y-3">
              {steps.map((label, index) => {
                const active = index === step
                const completed = index < step

                return (
                  <div
                    key={label}
                    className={[
                      'flex items-center gap-3 rounded-2xl border px-3 py-3',
                      active
                        ? 'border-black bg-white shadow-sm dark:border-white dark:bg-zinc-950'
                        : 'border-zinc-200 bg-transparent dark:border-zinc-800',
                    ].join(' ')}
                  >
                    <div
                      className={[
                        'flex h-8 w-8 items-center justify-center rounded-full text-sm font-semibold',
                        completed || active
                          ? 'bg-black text-white dark:bg-white dark:text-black'
                          : 'bg-zinc-200 text-zinc-700 dark:bg-zinc-800 dark:text-zinc-300',
                      ].join(' ')}
                    >
                      {completed ? <Check className="h-4 w-4" /> : index + 1}
                    </div>
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-zinc-900 dark:text-zinc-100">
                        {label}
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>

            <div className="mt-6 rounded-2xl border border-amber-200 bg-amber-50 p-4 dark:border-amber-900/60 dark:bg-amber-950/30">
              <div className="flex items-start gap-2">
                <TriangleAlert className="mt-0.5 h-4 w-4 text-amber-700 dark:text-amber-400" />
                <div className="text-sm text-amber-800 dark:text-amber-300">
                  Aquí configuras el <strong>cerebro de tu Agente Master</strong> y su{' '}
                  <strong>knowledge base</strong>.
                </div>
              </div>
            </div>
          </aside>

          <section className="flex min-h-0 flex-col">
            <div className="flex-1 overflow-y-auto p-6 md:p-8">
              <AnimatePresence mode="wait">
                <motion.div
                  key={step}
                  initial={{ opacity: 0, x: 24 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -24 }}
                  transition={{ duration: 0.2 }}
                >
                  {step === 0 && (
                    <StepShell
                      title="Personalidad e identidad del Master"
                      description="Esto define cómo piensa y cómo se comunica el cerebro principal de la comunidad."
                    >
                      <div className="grid gap-4 md:grid-cols-2">
                        {tonePresets.map(preset => (
                          <button
                            key={preset.id}
                            type="button"
                            onClick={() => selectTone(preset)}
                            className={[
                              'rounded-2xl border p-4 text-left transition',
                              data.toneId === preset.id
                                ? 'border-black bg-zinc-50 shadow-sm dark:border-white dark:bg-zinc-900'
                                : 'border-zinc-200 hover:border-zinc-400 hover:bg-zinc-50/60 dark:border-zinc-800 dark:hover:border-zinc-600 dark:hover:bg-zinc-900/70',
                            ].join(' ')}
                          >
                            <div className="flex items-start justify-between gap-3">
                              <div>
                                <div className="font-medium text-zinc-900 dark:text-zinc-100">
                                  {preset.title}
                                </div>
                                <div className="mt-1 text-sm text-zinc-600 dark:text-zinc-400">
                                  {preset.description}
                                </div>
                              </div>
                              {data.toneId === preset.id ? (
                                <Check className="h-4 w-4 shrink-0 text-zinc-900 dark:text-zinc-100" />
                              ) : null}
                            </div>
                          </button>
                        ))}
                      </div>

                      <div className="grid gap-5 md:grid-cols-2">
                        <div className="space-y-2">
                          <label className="text-sm font-medium text-zinc-800 dark:text-zinc-200">
                            Nombre del Master
                          </label>
                          <input
                            className="w-full rounded-xl border border-zinc-300 bg-white px-3 py-2.5 text-zinc-900 outline-none transition focus:border-black dark:border-zinc-700 dark:bg-zinc-950 dark:text-zinc-100 dark:focus:border-white"
                            value={data.masterName}
                            onChange={e =>
                              setData(prev => ({ ...prev, masterName: e.target.value }))
                            }
                            placeholder="Ej. KIN IA Sponsor"
                          />
                        </div>

                        <div className="space-y-2">
                          <label className="text-sm font-medium text-zinc-800 dark:text-zinc-200">
                            Vista previa del tono
                          </label>
                          <div className="min-h-[48px] rounded-xl border border-zinc-200 bg-zinc-50 px-3 py-2.5 text-sm text-zinc-700 dark:border-zinc-800 dark:bg-zinc-900 dark:text-zinc-300">
                            {data.tonePrompt || 'Selecciona una personalidad para previsualizar el tono.'}
                          </div>
                        </div>
                      </div>
                    </StepShell>
                  )}

                  {step === 1 && (
                    <StepShell
                      title="Misión del Agente Master"
                      description="Aquí defines para qué existe el Master y qué significa que está haciendo bien su trabajo."
                    >
                      <div className="space-y-2">
                        <label className="flex items-center gap-2 text-sm font-medium text-zinc-800 dark:text-zinc-200">
                          <Target className="h-4 w-4" />
                          Misión principal
                        </label>
                        <textarea
                          className="h-32 w-full rounded-xl border border-zinc-300 bg-white px-3 py-3 text-zinc-900 outline-none transition focus:border-black dark:border-zinc-700 dark:bg-zinc-950 dark:text-zinc-100 dark:focus:border-white"
                          value={data.mission}
                          onChange={e =>
                            setData(prev => ({ ...prev, mission: e.target.value }))
                          }
                          placeholder="Ej. Dar la bienvenida, acompañar, formar y orientar a los miembros de la comunidad..."
                        />
                      </div>

                      <div className="space-y-2">
                        <label className="text-sm font-medium text-zinc-800 dark:text-zinc-200">
                          ¿Cómo se ve el éxito del Master?
                        </label>
                        <textarea
                          className="h-28 w-full rounded-xl border border-zinc-300 bg-white px-3 py-3 text-zinc-900 outline-none transition focus:border-black dark:border-zinc-700 dark:bg-zinc-950 dark:text-zinc-100 dark:focus:border-white"
                          value={data.successDefinition}
                          onChange={e =>
                            setData(prev => ({
                              ...prev,
                              successDefinition: e.target.value,
                            }))
                          }
                          placeholder="Ej. El usuario recibe claridad, sigue el sistema correcto y avanza al siguiente paso sin confusión."
                        />
                      </div>
                    </StepShell>
                  )}

                  {step === 2 && (
                    <StepShell
                      title="Qué temas domina el Master"
                      description="Selecciona las áreas transversales que este Master debe manejar directamente."
                    >
                      <div className="grid gap-4 md:grid-cols-2">
                        {businessAreas.map(area => (
                          <MultiSelectIconCard
                            key={area.id}
                            id={area.id}
                            title={area.title}
                            description={area.description}
                            icon={area.icon}
                            selected={data.businessAreas.includes(area.id)}
                            onClick={toggleBusinessArea}
                          />
                        ))}
                      </div>

                      <div className="space-y-2">
                        <label className="flex items-center gap-2 text-sm font-medium text-zinc-800 dark:text-zinc-200">
                          <FileStack className="h-4 w-4" />
                          Resumen del núcleo de conocimiento del Master
                        </label>
                        <textarea
                          className="h-36 w-full rounded-xl border border-zinc-300 bg-white px-3 py-3 text-zinc-900 outline-none transition focus:border-black dark:border-zinc-700 dark:bg-zinc-950 dark:text-zinc-100 dark:focus:border-white"
                          value={data.knowledgeCore}
                          onChange={e =>
                            setData(prev => ({ ...prev, knowledgeCore: e.target.value }))
                          }
                          placeholder="Ej. Debe conocer la comunidad, filosofía, productos, onboarding, agenda y objeciones frecuentes."
                        />
                      </div>

                      <div className="space-y-3 rounded-2xl border border-zinc-200 p-4 dark:border-zinc-800">
                        <div className="text-sm font-medium text-zinc-900 dark:text-zinc-100">
                          Comportamiento al responder
                        </div>

                        <label className="flex items-start gap-3 text-sm text-zinc-700 dark:text-zinc-300">
                          <input
                            type="checkbox"
                            className="mt-1"
                            checked={data.shouldAskBeforeAnswer}
                            onChange={e =>
                              setData(prev => ({
                                ...prev,
                                shouldAskBeforeAnswer: e.target.checked,
                              }))
                            }
                          />
                          Antes de recomendar algo importante, el Master debe hacer una o más
                          preguntas para entender contexto, etapa o necesidad del usuario.
                        </label>
                      </div>
                    </StepShell>
                  )}

                  {step === 3 && (
                    <StepShell
                      title="Límites, seguridad y escalamiento"
                      description="Esto protege la calidad del agente y evita que improvise donde no debe."
                    >
                      <div className="space-y-3">
                        <div className="flex items-center gap-2 text-sm font-medium text-zinc-800 dark:text-zinc-200">
                          <Shield className="h-4 w-4" />
                          Reglas base del Master
                        </div>

                        <div className="grid gap-3">
                          {masterRulesBase.map(rule => (
                            <label
                              key={rule}
                              className="flex items-start gap-3 rounded-2xl border border-zinc-200 p-4 text-sm text-zinc-700 dark:border-zinc-800 dark:text-zinc-300"
                            >
                              <input
                                type="checkbox"
                                className="mt-1"
                                checked={data.rules.includes(rule)}
                                onChange={() => toggleRule(rule)}
                              />
                              <span>{rule}</span>
                            </label>
                          ))}
                        </div>

                        <textarea
                          className="h-28 w-full rounded-xl border border-zinc-300 bg-white px-3 py-3 text-zinc-900 outline-none transition focus:border-black dark:border-zinc-700 dark:bg-zinc-950 dark:text-zinc-100 dark:focus:border-white"
                          value={data.extraRules}
                          onChange={e =>
                            setData(prev => ({ ...prev, extraRules: e.target.value }))
                          }
                          placeholder="Reglas adicionales, una por línea"
                        />
                      </div>

                      <div className="space-y-3 pt-2">
                        <div className="text-sm font-medium text-zinc-800 dark:text-zinc-200">
                          Cuándo debe escalar
                        </div>

                        <div className="grid gap-3">
                          {escalationTriggersBase.map(rule => (
                            <label
                              key={rule}
                              className="flex items-start gap-3 rounded-2xl border border-zinc-200 p-4 text-sm text-zinc-700 dark:border-zinc-800 dark:text-zinc-300"
                            >
                              <input
                                type="checkbox"
                                className="mt-1"
                                checked={data.escalationTriggers.includes(rule)}
                                onChange={() => toggleEscalation(rule)}
                              />
                              <span>{rule}</span>
                            </label>
                          ))}
                        </div>

                        <textarea
                          className="h-24 w-full rounded-xl border border-zinc-300 bg-white px-3 py-3 text-zinc-900 outline-none transition focus:border-black dark:border-zinc-700 dark:bg-zinc-950 dark:text-zinc-100 dark:focus:border-white"
                          value={data.extraEscalation}
                          onChange={e =>
                            setData(prev => ({
                              ...prev,
                              extraEscalation: e.target.value,
                            }))
                          }
                          placeholder="Otros casos de escalamiento, uno por línea"
                        />
                      </div>
                    </StepShell>
                  )}

                  {step === 4 && (
                    <StepShell
                      title="Routing y arquitectura futura"
                      description="Aquí defines cómo se moverán las conversaciones y qué especialistas tendrá sentido crear después."
                    >
                      <div className="space-y-3">
                        <div className="flex items-center gap-2 text-sm font-medium text-zinc-800 dark:text-zinc-200">
                          <Route className="h-4 w-4" />
                          Qué debe hacer el Master en conversación
                        </div>

                        <div className="grid gap-3 md:grid-cols-2">
                          {routingPresets.map(item => (
                            <ToggleCard
                              key={item.id}
                              item={item}
                              selected={data.routing.includes(item.id)}
                              onClick={() => toggleRouting(item.id)}
                            />
                          ))}
                        </div>
                      </div>

                      <div className="space-y-3 pt-2">
                        <div className="flex items-center gap-2 text-sm font-medium text-zinc-800 dark:text-zinc-200">
                          <MessageSquareMore className="h-4 w-4" />
                          Preguntas iniciales sugeridas
                        </div>

                        <div className="flex flex-wrap gap-2">
                          {onboardingQuestionPresets.map(question => (
                            <SmallPill
                              key={question}
                              active={data.onboardingQuestions.includes(question)}
                              label={question}
                              onClick={() => toggleOnboardingQuestion(question)}
                            />
                          ))}
                        </div>

                        <textarea
                          className="h-24 w-full rounded-xl border border-zinc-300 bg-white px-3 py-3 text-zinc-900 outline-none transition focus:border-black dark:border-zinc-700 dark:bg-zinc-950 dark:text-zinc-100 dark:focus:border-white"
                          value={data.extraOnboardingQuestions}
                          onChange={e =>
                            setData(prev => ({
                              ...prev,
                              extraOnboardingQuestions: e.target.value,
                            }))
                          }
                          placeholder="Más preguntas iniciales, una por línea"
                        />
                      </div>

                      <div className="space-y-3 pt-2">
                        <div className="text-sm font-medium text-zinc-800 dark:text-zinc-200">
                          Agentes especializados recomendados para crear después
                        </div>

                        <div className="grid gap-3 md:grid-cols-2">
                          {specialistSuggestions.map(item => (
                            <MultiSelectIconCard
                              key={item.id}
                              id={item.id}
                              title={item.title}
                              description={item.description}
                              icon={item.icon}
                              selected={data.specialistSuggestions.includes(item.id)}
                              onClick={toggleSpecialist}
                            />
                          ))}
                        </div>
                      </div>
                    </StepShell>
                  )}

                  {step === 5 && (
                    <StepShell
                      title="Base de conocimiento del Master"
                      description="Sube notas, URLs y documentos de esta comunidad. Todo quedará ligado a este agentId como conocimiento del Master."
                    >
                      <div className="space-y-6">
                        <div className="space-y-2">
                          <label className="text-sm font-medium text-zinc-800 dark:text-zinc-200">
                            Notas base
                          </label>
                          <textarea
                            className="h-44 w-full rounded-xl border border-zinc-300 bg-white px-3 py-3 text-zinc-900 outline-none transition focus:border-black dark:border-zinc-700 dark:bg-zinc-950 dark:text-zinc-100 dark:focus:border-white"
                            value={data.notes}
                            onChange={e =>
                              setData(prev => ({ ...prev, notes: e.target.value }))
                            }
                            placeholder="Pega aquí información importante sobre la comunidad, productos, sistema, FAQs, objeciones, metodología, etc."
                          />
                        </div>

                        <div className="space-y-3">
                          <label className="text-sm font-medium text-zinc-800 dark:text-zinc-200">
                            Links / URLs
                          </label>

                          <div className="flex gap-2">
                            <input
                              type="url"
                              className="h-11 flex-1 rounded-xl border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 outline-none transition focus:border-black dark:border-zinc-700 dark:bg-zinc-950 dark:text-zinc-100 dark:focus:border-white"
                              value={linkInput}
                              onChange={e => setLinkInput(e.target.value)}
                              placeholder="https://..."
                              autoCapitalize="none"
                              autoCorrect="off"
                              spellCheck={false}
                            />
                            <button
                              type="button"
                              onClick={addLink}
                              className="rounded-2xl bg-cyan-500 px-4 py-2 text-sm font-semibold text-slate-950 transition hover:bg-cyan-400"
                            >
                              Agregar
                            </button>
                          </div>

                          <div className="space-y-2">
                            {data.links.map((link, i) => (
                              <div
                                key={`${link}-${i}`}
                                className="flex items-center justify-between rounded-2xl border border-zinc-200 bg-white/70 p-3 text-sm dark:border-zinc-800 dark:bg-zinc-900/50"
                              >
                                <div className="flex items-center gap-2 overflow-hidden">
                                  <LinkIcon size={14} />
                                  <span className="truncate">{link}</span>
                                </div>

                                <button
                                  type="button"
                                  onClick={() => removeLink(i)}
                                  className="text-red-600 hover:text-red-800"
                                >
                                  <Trash2 size={14} />
                                </button>
                              </div>
                            ))}
                          </div>
                        </div>

                        <div className="space-y-3">
                          <label className="text-sm font-medium text-zinc-800 dark:text-zinc-200">
                            Documentos
                          </label>

                          <label className="flex cursor-pointer items-center gap-2 rounded-2xl border border-zinc-200 bg-white/70 p-4 transition hover:border-cyan-500/30 dark:border-zinc-800 dark:bg-zinc-900/50">
                            {uploading ? <Loader2 className="animate-spin" /> : <Upload />}
                            <span>{uploading ? 'Subiendo...' : 'Subir documentos (PDF, DOCX, TXT)'}</span>
                            <input
                              type="file"
                              multiple
                              hidden
                              onChange={e => handleFiles(e.target.files)}
                            />
                          </label>

                          {data.documents.length > 0 && (
                            <div className="space-y-2">
                              {data.documents.map((doc, i) => (
                                <div
                                  key={`${doc.name}-${i}`}
                                  className="flex items-center justify-between rounded-2xl border border-zinc-200 bg-white/70 p-3 text-sm dark:border-zinc-800 dark:bg-zinc-900/50"
                                >
                                  <div className="flex items-center gap-2">
                                    <FileText size={14} />
                                    <span>{doc.name}</span>
                                  </div>

                                  <div className="text-xs text-zinc-500 dark:text-zinc-400">
                                    {doc.status || 'pending'}
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    </StepShell>
                  )}

                  {step === 6 && (
                    <StepShell
                      title="Confirmación del cerebro Master"
                      description="Esto guardará la configuración estratégica del Master y su knowledge base documental."
                    >
                      <div className="grid gap-4 xl:grid-cols-[1fr_1.1fr]">
                        <div className="space-y-4">
                          <div className="rounded-2xl border border-zinc-200 p-4 dark:border-zinc-800">
                            <div className="text-sm font-medium text-zinc-900 dark:text-zinc-100">
                              Resumen ejecutivo
                            </div>

                            <div className="mt-3 space-y-3 text-sm text-zinc-700 dark:text-zinc-300">
                              <div><strong>Nombre:</strong> {data.masterName || '—'}</div>
                              <div>
                                <strong>Tono:</strong>{' '}
                                {tonePresets.find(t => t.id === data.toneId)?.title || '—'}
                              </div>
                              <div><strong>Misión:</strong> {data.mission || '—'}</div>
                              <div><strong>Éxito esperado:</strong> {data.successDefinition || '—'}</div>
                              <div>
                                <strong>Áreas:</strong>{' '}
                                {businessAreas
                                  .filter(a => data.businessAreas.includes(a.id))
                                  .map(a => a.title)
                                  .join(', ') || '—'}
                              </div>
                              <div>
                                <strong>Routing:</strong>{' '}
                                {routingPresets
                                  .filter(r => data.routing.includes(r.id))
                                  .map(r => r.title)
                                  .join(', ') || '—'}
                              </div>
                              <div>
                                <strong>Especialistas sugeridos:</strong>{' '}
                                {specialistSuggestions
                                  .filter(s => data.specialistSuggestions.includes(s.id))
                                  .map(s => s.title)
                                  .join(', ') || '—'}
                              </div>
                              <div><strong>Notas:</strong> {data.notes ? 'Sí' : 'No'}</div>
                              <div><strong>Links:</strong> {data.links.length}</div>
                              <div><strong>Documentos:</strong> {data.documents.length}</div>
                            </div>
                          </div>
                        </div>

                        <div className="rounded-2xl border border-zinc-200 bg-zinc-50 p-4 dark:border-zinc-800 dark:bg-zinc-900">
                          <div className="mb-3 text-sm font-medium text-zinc-900 dark:text-zinc-100">
                            Borrador del Master Prompt / Brain Blueprint
                          </div>

                          <pre className="max-h-[520px] overflow-auto whitespace-pre-wrap rounded-xl bg-white p-4 text-xs leading-6 text-zinc-700 dark:bg-zinc-950 dark:text-zinc-300">
                            {masterPromptDraft}
                          </pre>
                        </div>
                      </div>
                    </StepShell>
                  )}
                </motion.div>
              </AnimatePresence>
            </div>

            <footer className="border-t bg-white px-6 py-4 dark:border-zinc-800 dark:bg-zinc-950 md:px-8">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="text-sm text-zinc-500 dark:text-zinc-400">
                  Paso {step + 1} de {steps.length}
                </div>

                <div className="flex items-center justify-end gap-3">
                  <button
                    type="button"
                    disabled={step === 0 || loading || uploading || savingKnowledge}
                    onClick={() => setStep(prev => Math.max(prev - 1, 0))}
                    className="inline-flex items-center gap-2 rounded-xl border border-zinc-300 px-4 py-2 text-sm text-zinc-700 transition hover:border-zinc-500 disabled:cursor-not-allowed disabled:opacity-40 dark:border-zinc-700 dark:text-zinc-300 dark:hover:border-zinc-500"
                  >
                    <ChevronLeft className="h-4 w-4" />
                    Atrás
                  </button>

                  {step < steps.length - 1 ? (
                    <button
                      type="button"
                      disabled={!canContinue || loading || uploading || savingKnowledge}
                      onClick={() => setStep(prev => Math.min(prev + 1, steps.length - 1))}
                      className="inline-flex items-center gap-2 rounded-xl bg-black px-5 py-2.5 text-sm font-medium text-white transition hover:bg-zinc-800 disabled:cursor-not-allowed disabled:opacity-40 dark:bg-white dark:text-black dark:hover:bg-zinc-200"
                    >
                      Siguiente
                      <ChevronRight className="h-4 w-4" />
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={handleFinish}
                      disabled={loading || uploading || savingKnowledge}
                      className="inline-flex items-center gap-2 rounded-xl bg-green-600 px-5 py-2.5 text-sm font-medium text-white transition hover:bg-green-700 disabled:cursor-not-allowed disabled:opacity-40"
                    >
                      {loading || savingKnowledge ? 'Guardando Master…' : 'Activar Agente Master'}
                    </button>
                  )}
                </div>
              </div>
            </footer>
          </section>
        </div>
      </motion.div>
    </motion.div>
  )
}